<script lang="ts">
  import { slide } from "svelte/transition";
  import { SECS_PER_PAGE, type UploadMeta } from "@piano/shared";
  import { library } from "../lib/stores/library.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Icon from "./Icon.svelte";
  import Sheet from "./Sheet.svelte";

  interface Item {
    file: File;
    progress: number;
    state: "wartet" | "lädt" | "fertig" | "fehler";
    error?: string;
  }

  let items = $state<Item[]>([]);
  let favorite = $state(false);
  let categoryIds = $state<string[]>([]);
  let addToActive = $state(true);
  let secs = $state<number | null>(null);
  let dragging = $state(false);
  let busy = $state(false);
  let input: HTMLInputElement | undefined = $state();

  function addFiles(list: FileList | null): void {
    if (!list) return;
    const pdfs = [...list].filter((f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
    if (pdfs.length < list.length) ui.toast("Nur PDF-Dateien werden übernommen", "error");
    items = [...items, ...pdfs.map((file) => ({ file, progress: 0, state: "wartet" as const }))];
  }

  function toggleCategory(id: string): void {
    categoryIds = categoryIds.includes(id) ? categoryIds.filter((c) => c !== id) : [...categoryIds, id];
  }

  async function start(): Promise<void> {
    busy = true;
    const meta: UploadMeta = {
      ...(favorite ? { favorite: true } : {}),
      ...(categoryIds.length ? { categoryIds } : {}),
      ...(addToActive && playlists.activeId ? { playlistIds: [playlists.activeId] } : {}),
      ...(secs
        ? { secsPerPage: Math.round(Math.min(SECS_PER_PAGE.max, Math.max(SECS_PER_PAGE.min, secs))) }
        : {}),
    };
    let ok = 0;
    for (const item of items) {
      if (item.state === "fertig") continue;
      item.state = "lädt";
      try {
        await library.upload(item.file, meta, (fraction) => (item.progress = fraction));
        item.state = "fertig";
        item.progress = 1;
        ok += 1;
      } catch (error) {
        item.state = "fehler";
        item.error = error instanceof Error ? error.message : "Fehlgeschlagen";
      }
    }
    busy = false;
    if (meta.playlistIds) void playlists.load();
    if (ok) ui.toast(ok === 1 ? "PDF hochgeladen" : `${ok} PDFs hochgeladen`, "success");
    if (items.every((i) => i.state === "fertig")) ui.uploadOpen = false;
  }
</script>

<Sheet title="Noten hinzufügen" onclose={() => !busy && (ui.uploadOpen = false)} width={520}>
  <div class="upload">
    <h2>Noten hinzufügen</h2>
    <button
      type="button"
      class="drop"
      class:dragging
      onclick={() => input?.click()}
      ondragover={(e) => (e.preventDefault(), (dragging = true))}
      ondragleave={() => (dragging = false)}
      ondrop={(e) => {
        e.preventDefault();
        dragging = false;
        addFiles(e.dataTransfer?.files ?? null);
      }}
    >
      <span class="drop-icon"><Icon name="upload" size={28} /></span>
      <span class="drop-title">PDF auswählen oder hierher ziehen</span>
      <span class="t3 drop-sub">Mehrere Dateien möglich · bis 200 MB pro Datei</span>
    </button>
    <input
      bind:this={input}
      class="sr-only"
      type="file"
      accept="application/pdf,.pdf"
      multiple
      onchange={(e) => addFiles(e.currentTarget.files)}
    />

    {#if items.length}
      <ul class="files" transition:slide>
        {#each items as item (item.file.name + item.file.size)}
          <li class="file" transition:slide>
            <span class="name ellipsis">{item.file.name}</span>
            <span class="state {item.state}">{item.state === "fehler" ? item.error : item.state}</span>
            <span class="bar"><span style:width="{Math.round(item.progress * 100)}%"></span></span>
          </li>
        {/each}
      </ul>
    {/if}

    <div class="opts">
      <button
        type="button"
        class="opt"
        role="switch"
        aria-checked={favorite}
        onclick={() => (favorite = !favorite)}
      >
        <span>Als Favorit markieren</span><span class="switch" aria-checked={favorite}></span>
      </button>
      {#if playlists.active}
        <button
          type="button"
          class="opt"
          role="switch"
          aria-checked={addToActive}
          onclick={() => (addToActive = !addToActive)}
        >
          <span>Zu „{playlists.active.name}“ hinzufügen</span><span class="switch" aria-checked={addToActive}
          ></span>
        </button>
      {/if}
      {#if library.categories.length}
        <div class="cats">
          {#each library.categories as category (category.id)}
            <button
              type="button"
              class="chip"
              aria-pressed={categoryIds.includes(category.id)}
              onclick={() => toggleCategory(category.id)}
            >
              <span class="dot" style:background={category.color}></span>{category.name}
            </button>
          {/each}
        </div>
      {/if}
      <label class="field">
        Tempo für Auto-Scroll (Sekunden pro Seite, optional)
        <input
          class="input"
          type="number"
          min={SECS_PER_PAGE.min}
          max={SECS_PER_PAGE.max}
          placeholder="z. B. 45"
          bind:value={secs}
        />
      </label>
    </div>

    <div class="actions">
      <button type="button" class="btn btn-ghost" disabled={busy} onclick={() => (ui.uploadOpen = false)}
        >Abbrechen</button
      >
      <button
        type="button"
        class="btn btn-primary"
        disabled={busy || !items.some((i) => i.state !== "fertig")}
        onclick={start}
      >
        {busy ? "Lädt hoch …" : items.length > 1 ? `${items.length} PDFs hochladen` : "Hochladen"}
      </button>
    </div>
  </div>
</Sheet>

<style>
  .upload {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 8px;
  }
  h2 {
    font-size: 22px;
  }
  .drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 26px 16px;
    border-radius: var(--r-lg);
    border: 2px dashed var(--line-strong);
    transition:
      border-color var(--dur-fast),
      background var(--dur-fast),
      transform var(--dur-base) var(--ease-spring);
  }
  .drop:hover,
  .drop.dragging {
    border-color: var(--accent);
    background: var(--accent-soft);
  }
  .drop.dragging {
    transform: scale(1.02);
  }
  .drop-icon {
    color: var(--accent);
  }
  .drop-title {
    font-weight: 800;
  }
  .drop-sub {
    font-size: 12px;
    font-weight: 600;
  }
  .files {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .file {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px 10px;
    font-size: 13px;
    font-weight: 700;
  }
  .state {
    color: var(--text-3);
  }
  .state.fertig {
    color: var(--accent-3);
  }
  .state.fehler {
    color: var(--danger);
  }
  .bar {
    grid-column: 1 / -1;
    height: 4px;
    border-radius: 2px;
    background: var(--raised);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--accent);
    transition: width var(--dur-fast) linear;
  }
  .opts {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .opt {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 44px;
    font-weight: 700;
    text-align: left;
  }
  .cats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
</style>
